import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Camera, ArrowRight, ArrowLeft, X, Sparkles, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BrandLogo } from '@/components/BrandLogo';
import { saveTryDraft } from '@/lib/tryDraft';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';

type Step = 'compose' | 'preview' | 'gate';

export default function TryCreate() {
  const [step, setStep] = useState<Step>('compose');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // If already logged in, save draft and go to real create flow
  const handleAuthenticatedContinue = useCallback(() => {
    saveTryDraft({ recipientName, message, photoDataUrl: photoPreview ?? undefined });
    navigate('/create?from=try');
  }, [recipientName, message, photoPreview, navigate]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const canPreview = recipientName.trim().length > 0 && message.trim().length > 10;

  const handlePreview = () => {
    saveTryDraft({ recipientName, message, photoDataUrl: photoPreview ?? undefined });
    setStep('preview');
  };

  const handleContinueToShare = () => {
    if (user) {
      handleAuthenticatedContinue();
    } else {
      setStep('gate');
    }
  };

  const handleSignup = () => {
    saveTryDraft({ recipientName, message, photoDataUrl: photoPreview ?? undefined });
    navigate('/signup?from=try');
  };

  const handleLogin = () => {
    saveTryDraft({ recipientName, message, photoDataUrl: photoPreview ?? undefined });
    navigate('/login?from=try');
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      {/* Minimal header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/">
            <BrandLogo size="sm" />
          </Link>
          {step !== 'compose' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(step === 'gate' ? 'preview' : 'compose')}
              className="gap-1 text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-8 sm:py-12">
        <AnimatePresence mode="wait">
          {/* ─── STEP 1: COMPOSE ─── */}
          {step === 'compose' && (
            <motion.div
              key="compose"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-lg space-y-6"
            >
              {/* Headline */}
              <div className="text-center space-y-2">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                  Before it's too late…
                </h1>
                <p className="text-muted-foreground">
                  say what you've never said.
                </p>
              </div>

              {/* Photo upload */}
              <div className="flex justify-center">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                {photoPreview ? (
                  <div className="relative group">
                    <img
                      src={photoPreview}
                      alt="Upload preview"
                      className="h-28 w-28 rounded-2xl object-cover border-2 border-primary/20"
                    />
                    <button
                      onClick={() => setPhotoPreview(null)}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="h-28 w-28 rounded-2xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Camera className="h-6 w-6" />
                    <span className="text-xs">Add photo</span>
                  </button>
                )}
              </div>

              {/* Recipient name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Who is this for?</label>
                <Input
                  placeholder="Mom, Dad, Best Friend, Partner…"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="text-base"
                  maxLength={60}
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Edit3 className="h-3.5 w-3.5 text-primary" />
                  Write something you've never said before…
                </label>
                <Textarea
                  placeholder="There are things I should have said sooner…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[140px] text-base resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {message.length}/2000
                </p>
              </div>

              {/* CTA */}
              <Button
                size="lg"
                onClick={handlePreview}
                disabled={!canPreview}
                className="w-full gap-2 text-base py-6 rounded-2xl"
              >
                Preview Your Message
                <ArrowRight className="h-4 w-4" />
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                No account needed to preview. Free forever.
              </p>
            </motion.div>
          )}

          {/* ─── STEP 2: PREVIEW ─── */}
          {step === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-lg space-y-6"
            >
              <p className="text-center text-sm text-muted-foreground italic">
                Imagine they saw this…
              </p>

              {/* Preview card */}
              <div className="relative mx-auto">
                <div className="absolute -inset-3 bg-gradient-to-br from-primary/10 via-secondary/8 to-primary/5 rounded-3xl blur-2xl" />
                <div className="relative bg-card rounded-2xl border border-border/40 shadow-2xl overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-primary via-secondary to-primary" />

                  {photoPreview && (
                    <div className="w-full aspect-video overflow-hidden">
                      <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className="p-8 space-y-4">
                    <p className="text-sm font-medium text-primary tracking-wide uppercase">
                      To: {recipientName}
                    </p>
                    <p className="text-lg font-serif text-foreground leading-relaxed italic whitespace-pre-wrap">
                      {message}
                    </p>
                    <p className="text-sm text-muted-foreground pt-2">— With love ❤️</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Button
                size="lg"
                onClick={handleContinueToShare}
                className="w-full gap-2 text-base py-6 rounded-2xl"
              >
                <Heart className="h-5 w-5" fill="currentColor" />
                Continue to Share
                <ArrowRight className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                onClick={() => setStep('compose')}
                className="w-full text-muted-foreground"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit Message
              </Button>
            </motion.div>
          )}

          {/* ─── STEP 3: SIGNUP GATE ─── */}
          {step === 'gate' && (
            <motion.div
              key="gate"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md space-y-8 text-center"
            >
              <div className="space-y-3">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mx-auto">
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Your message is ready ❤️
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Create a free account to save and share it with {recipientName || 'someone special'}.
                </p>
              </div>

              {/* Mini preview reminder */}
              <div className="bg-card/50 border border-border/40 rounded-xl p-4 text-left">
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Your message to {recipientName}</p>
                <p className="text-sm text-foreground italic line-clamp-3">
                  {message}
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  size="lg"
                  onClick={handleSignup}
                  className="w-full gap-2 text-base py-6 rounded-2xl"
                >
                  <Heart className="h-5 w-5" fill="currentColor" />
                  Create Free Account
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleLogin}
                  className="w-full text-base py-6 rounded-2xl"
                >
                  I already have an account
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('compose')}
                className="text-muted-foreground"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit Message
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
